import { useMediaQuery } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

// Long enough to find the row after the scroll settles, short enough not to linger as decoration.
const HIGHLIGHT_DURATION_MS = 2500

export function portRowElementId(portId: string): string {
  return `port-row-${portId}`
}

/**
 * Lets one port row point at another it relates to.
 *
 * Grouping by controller puts most related ports side by side, but the data model allows a control
 * port to be measured by a metering port on a *different* controller — which the grouping then
 * separates. Those are exactly the references worth jumping to.
 *
 * Moving focus to the target row is the part that carries to keyboard and screen reader users; the
 * transient highlight is only the sighted equivalent, and is paired with an outline so it does not
 * depend on colour alone.
 */
export function usePortHighlight() {
  const [highlightedPortId, setHighlightedPortId] = useState<string | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  const showPort = useCallback(
    (portId: string) => {
      const row = document.getElementById(portRowElementId(portId))

      // No row means nothing to scroll to, nothing to focus and nothing the highlight could mark —
      // recording it anyway would leave a click that did nothing looking like it succeeded. Callers
      // gate on navigability so this should not be reachable; it is the backstop, not the guard.
      if (row === null) {
        return
      }

      // jsdom implements neither.
      row.scrollIntoView?.({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' })
      row.focus?.({ preventScroll: true })

      setHighlightedPortId(portId)

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => setHighlightedPortId(null), HIGHLIGHT_DURATION_MS)
    },
    [prefersReducedMotion],
  )

  return { highlightedPortId, showPort }
}
